
import { Component, h, Prop } from '@stencil/core';

@Component({
    tag: 'ti-download-excel-button',
    shadow: true
})
export class DownloadExcelButton {

    /**
     * Color to be passed down to the ti-button instance.
     *
     * @type {'primary' | 'text' | 'plain'}
     * @default 'plain'
     */
    @Prop() appearance?: 'primary' | 'text' | 'plain' = 'plain';

    /**
     * The file name to use for the download file.
     *
     * @type {string}
     * @default 'download.csv'
     */
    @Prop() fileName: string = 'download.csv';

    /**
     * The ID of the table that we're going to turn into a CSV.
     * NOTE: It has to be a table element.
     *
     * @type {string}
     */
    @Prop() targetTableId: string;

    /**
     * Internal copy of downloadjs 4.21
     * Allows us to handle creating a file for users to download.
     * MIT license.
     * @param window
     */
    private _downloadjs(window) {
        // download.js v4.21, by dandavis; 2008-2018. [MIT] see http://danml.com/download.html for tests/usage
        // NOTE: modified to remove URL handling
        return function (data, strFileName, strMimeType) {

            const self = window; // this script is only for browsers anyway...
            const defaultMime = 'application/octet-stream'; // this default mime also triggers iframe downloads
            const anchor = document.createElement('a');
            const toString = function (a) { return String(a); };
            const fileName = strFileName || 'download';
            let payload = data;
            let mimeType = strMimeType || defaultMime;
            let myBlob = (self.Blob || self.MozBlob || self.WebKitBlob || toString);
            myBlob = myBlob.call ? myBlob.bind(self) : Blob;

            if (String(this) === 'true') { //reverse arguments, allowing download.bind(true, "text/xml", "export.xml") to act as a callback
                payload = [payload, mimeType];
                mimeType = payload[0];
                payload = payload[1];
            }

            //go ahead and download dataURLs right away
            if (/^data:([\w+-]+\/[\w+.-]+)?[,;]/.test(payload)) {

                if (payload.length > (1024 * 1024 * 1.999) && myBlob !== toString) {
                    payload = dataUrlToBlob(payload);
                    mimeType = payload.type || defaultMime;
                } else {
                    return navigator.msSaveBlob ?  // IE10 can't do a[download], only Blobs:
                        navigator.msSaveBlob(dataUrlToBlob(payload), fileName) :
                        saver(payload); // everyone else can save dataURLs un-processed
                }

            } else {//not data url, is it a string with special needs?
                if (/([\x80-\xff])/.test(payload)) {
                    let i = 0;
                    const tempUiArr = new Uint8Array(payload.length);
                    const mx = tempUiArr.length;
                    for (i; i < mx; ++i) tempUiArr[i] = payload.charCodeAt(i);
                    payload = new myBlob([tempUiArr], { type: mimeType });
                }
            }
            const blob = payload instanceof myBlob ?
                payload :
                new myBlob([payload], { type: mimeType });

            function dataUrlToBlob(strUrl) {
                const parts = strUrl.split(/[:;,]/);
                const type = parts[1];
                const indexDecoder = strUrl.indexOf('charset') > 0 ? 3 : 2;
                const decoder = parts[indexDecoder] == 'base64' ? atob : decodeURIComponent;
                const binData = decoder(parts.pop());
                const mx = binData.length;
                const uiArr = new Uint8Array(mx);

                for (let i = 0; i < mx; ++i) uiArr[i] = binData.charCodeAt(i);

                return new myBlob([uiArr], { type: type });
            }

            function saver(url, winMode?) {

                if ('download' in anchor) { //html5 A[download]
                    anchor.href = url;
                    anchor.setAttribute('download', fileName);
                    anchor.className = 'download-js-link';
                    anchor.innerHTML = 'downloading...';
                    anchor.style.display = 'none';
                    anchor.addEventListener('click', function (e) {
                        e.stopPropagation();
                        //  this.removeEventListener('click', arguments.callee);
                    });
                    document.body.appendChild(anchor);
                    setTimeout(function () {
                        anchor.click();
                        document.body.removeChild(anchor);
                        if (winMode === true) { setTimeout(function () { self.URL.revokeObjectURL(anchor.href); }, 250); }
                    }, 66);
                    return true;
                }

                // handle non-a[download] safari as best we can:
                if (/(Version)\/(\d+)\.(\d+)(?:\.(\d+))?.*Safari\//.test(navigator.userAgent)) {
                    if (/^data:/.test(url)) url = 'data:' + url.replace(/^data:([\w/\-+]+)/, defaultMime);
                    if (!window.open(url)) { // popup blocked, offer direct download:
                        if (confirm('Displaying New Document\n\nUse Save As... to download, then click back to return to this page.')) { location.href = url; }
                    }
                    return true;
                }

                //do iframe dataURL download (old ch+FF):
                const f = document.createElement('iframe');
                document.body.appendChild(f);

                if (!winMode && /^data:/.test(url)) { // force a mime that will download:
                    url = 'data:' + url.replace(/^data:([\w/\-+]+)/, defaultMime);
                }
                f.src = url;
                setTimeout(function () { document.body.removeChild(f); }, 333);

            }//end saver

            if (navigator.msSaveBlob) { // IE10+ : (has Blob, but not a[download] or URL)
                return navigator.msSaveBlob(blob, fileName);
            }

            if (self.URL) { // simple fast and modern way using Blob and URL:
                saver(self.URL.createObjectURL(blob), true);
            } else {
                // handle non-Blob()+non-URL browsers:
                if (typeof blob === 'string' || blob.constructor === toString) {
                    try {
                        return saver('data:' + mimeType + ';base64,' + self.btoa(blob));
                    } catch (y) {
                        return saver('data:' + mimeType + ',' + encodeURIComponent(blob));
                    }
                }

                // Blob but not URL support:
                const reader = new FileReader();
                reader.onload = function () {
                    saver(this.result);
                };
                reader.readAsDataURL(blob);
            }
            return true;
        };
    }

    /**
     * Function for handling when user clicks this button
     */
    private _handleClick() {
        const targetHTML = document.getElementById(this.targetTableId);
        if (!targetHTML || targetHTML.tagName !== 'TABLE') return;

        const raw = this._htmlToCommaArray(targetHTML);
        // tack on a note for users to not worry about not seeing all the data.
        raw.push('');
        raw.push('NOTE: Some field data may be hidden. Double-click row borders to expand.');

        // call downloadJS and start the download for the user
        this._downloadjs(window)(raw.join('\n'), this.fileName, 'text/csv');
    }

    /**
     * Function to help convert html table to an array of comma separated values.
     * @param html HTML of table to convert
     */
    private _htmlToCommaArray(tableHtml) {
        const raw = [];
        const rowList: NodeList = tableHtml.querySelectorAll('tr');
        for (let r = 0; r < rowList.length; r++) { // IE does not support NodeList.forEach
            const rowData = [];
            const row = rowList.item(r) as HTMLElement;
            if (row.querySelector('th')) {
                const headerList: NodeList = row.querySelectorAll('th');
                for (let h = 0; h < headerList.length; h++) { // IE does not support NodeList.forEach
                    const header = headerList.item(h);
                    rowData.push(`"${header.textContent.trim()}"`);
                }
                // make sure that this works in cn and jp
                rowData[1] = `"Order now"`;
            }
            if (row.querySelector('td')) {
                const cellList: NodeList = row.querySelectorAll('td');
                for (let c = 0; c < cellList.length; c++) { // IE does not support NodeList.forEach
                    const cell = cellList.item(c);
                    rowData.push(`"${cell.textContent.trim().replace(/[ ][ ]+/g,'').replace(/[\n\r][\n\r]+/g,'\n')}"`);
                }
                // handle href on second column
                const href = (cellList.item(1) as HTMLElement).innerHTML.match(/href="([^"]+)"/);
                if (href && href[1]) {
                    let url: string = href[1];
                    if (url.indexOf('//') === 0) {
                        url = 'https:' + url;
                    }
                    rowData[1] = `=HYPERLINK("${url}")`;
                }
            }
            raw.push(rowData.join(','));
        }
        return raw;
    }

    render() {
        return <ti-button
            appearance={ this.appearance }
            onClick={ () => this._handleClick() }
        >
            <slot />
        </ti-button>;
    }
}
